import { ChangeEvent, useState, useRef, useEffect } from 'react';
import { Box, Flex, Button, Divider, useOutsideClick } from '@chakra-ui/react';
import ChakraCustomCheckbox from './ChakraCustomCheckbox';
import NoSSR from '@components/UI/NoSSR';

export const DropdownFilter = ({
  title,
  selectedItem,
  items,
  handleSelectedItem,
  isMultiple = false,
}: {
  title: string;
  selectedItem: string | string[];
  items: {
    label: string;
    value: string;
  }[];
  isMultiple?: boolean;
  handleSelectedItem: (value: string | string[]) => void;
}) => {
  const [toggleFilters, setToggleFilters] = useState<boolean>(false);
  const [filterValue, setFilterValue] = useState<string | string[]>('all' || []);
  const filtersRef = useRef<any>(null);

  useOutsideClick({
    ref: filtersRef,
    handler: () => setToggleFilters(false),
  });

  useEffect(() => {
    if (selectedItem) {
      setFilterValue(selectedItem);
    }
  }, [selectedItem]);

  const handleApplyFilter = (value: string | string[]) => {
    setToggleFilters(false);
    handleSelectedItem(value);
  };

  const FilterButton = ({
    rotate,
    bgColor,
    children,
    ...props
  }: {
    rotate: boolean;
    bgColor: string;
    children: React.ReactNode;
    [x: string]: any;
  }) => {
    return (
      <Button
        px="3"
        py="0"
        w={{ base: '100%', md: '17rem', lg: '20.3125rem' }}
        h="12"
        fontWeight="normal"
        color="text.disabled"
        fontSize="1rem"
        borderWidth="0.0625rem"
        borderColor="ui.secondary"
        bgColor={bgColor}
        textAlign="left"
        justifyContent="flex-start"
        position="relative"
        _hover={{
          bgColor: 'white',
        }}
        _focus={{
          bgColor: 'white',
        }}
        _active={{
          bgColor: 'white',
        }}
        {...props}
      >
        {children}
        <Box
          position="absolute"
          right="1rem"
          transition="transform .3s ease-in-out"
          transform={`rotate(${rotate ? '180' : '0'}deg)`}
        >
          <svg
            width="17"
            height="16"
            viewBox="0 0 17 16"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M12.6667 6L8.85519 9.8112C8.80524 9.86122 8.73744 9.88933 8.66675 9.88933C8.59605 9.88933 8.52826 9.86122 8.4783 9.8112L4.66675 6"
              stroke="#2C2C2C"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </Box>
      </Button>
    );
  };

  const selectedItemTitle = () => {
    if (isMultiple) {
      return selectedItem.length > 0 ? (
        <Flex
          w="6"
          h="6"
          borderRadius="50%"
          bgColor="accent.main"
          color="white"
          justifyContent="center"
        >
          {selectedItem.length}
        </Flex>
      ) : (
        ''
      );
    } else {
      return (
        items.filter(
          (item) => (selectedItem as string).split('-').join(' ') === item.label.toLowerCase(),
        )[0]?.label || ''
      );
    }
  };

  const handleChecked = (
    filterValue: string | string[],
    item: { label: string; value: string },
  ) => {
    if (isMultiple) {
      return filterValue.includes(item.value);
    } else {
      return filterValue === item.value;
    }
  };

  const handleOnChange = (e: ChangeEvent<HTMLInputElement>, value: string) => {
    if (isMultiple) {
      let updatedList = Array.from(filterValue);
      if (e.target.checked) {
        updatedList = updatedList.concat(value);
      } else {
        updatedList.splice(filterValue.indexOf(value), 1);
      }
      setFilterValue(updatedList);
    } else {
      setFilterValue(value);
    }
  };

  const handleToggleFilters = () => {
    const scrollToEl = document.getElementById('filters');
    window.scrollTo({
      top: scrollToEl.getBoundingClientRect().top + window.pageYOffset - 115,
      behavior: 'smooth',
    });
    setToggleFilters(!toggleFilters);
  };

  return (
    <Box>
      <FilterButton
        onClick={() => handleToggleFilters()}
        rotate={toggleFilters}
        bgColor={selectedItemTitle() ? 'accent.lightest' : 'white'}
      >
        {title}&nbsp;
        <Box fontWeight="bold" color="text.primary">
          {selectedItemTitle()}
        </Box>
      </FilterButton>
      <Box position="relative">
        <Box
          position="absolute"
          top="0.625rem"
          left="0"
          zIndex="2"
          w={{ base: '100%', md: '17rem', lg: '20.3125rem' }}
          maxHeight={{
            base: toggleFilters ? filtersRef.current.scrollHeight : '0',
          }}
          transition="max-height .3s ease-in-out"
          overflow="hidden"
          ref={filtersRef}
          bgColor="white"
          boxShadow="var(--chakra-shadows-md)"
        >
          <Box borderWidth="0.0625rem" borderColor="ui.secondary">
            <Box maxHeight="400px" overflow="scroll">
              <NoSSR>
                {items.map((item) => (
                  <ChakraCustomCheckbox
                    key={item.label}
                    w="100%"
                    p="0.9375rem 0.75rem"
                    isChecked={handleChecked(filterValue, item)}
                    value={item.label}
                    onChange={(e) => handleOnChange(e, item.value)}
                    variant={isMultiple ? 'square' : 'circle'}
                  >
                    {item.label}
                  </ChakraCustomCheckbox>
                ))}
              </NoSSR>
            </Box>
            <Divider />
            <Box p="3">
              <Button
                w="100%"
                variant="outline"
                colorScheme="accent"
                onClick={() => handleApplyFilter(filterValue)}
              >
                Apply Filter
              </Button>
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};
